import { Type } from 'class-transformer';
import {
	IsArray,
	isEmail,
	IsEmail,
	IsEnum,
	IsNumber,
	IsObject,
	IsOptional,
	IsString,
	Validate,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';
import { EmailProviderType, EmailTemplateSystemSlug } from '../../enums';

@ValidatorConstraint({ name: 'isEmailAddressDtoOrString', async: false })
export class IsEmailAddressDtoOrString implements ValidatorConstraintInterface {
	public validate(value: unknown, args: ValidationArguments): boolean {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const { allowMultiple = false } = (args.constraints?.[0] ?? {}) as {
			allowMultiple?: boolean;
		};

		const isValid = (value: unknown): boolean => {
			if (typeof value === 'string') {
				return isEmail(value);
			}

			if (typeof value === 'object' && value != null && !Array.isArray(value)) {
				const dto = value as { email?: unknown; name?: unknown };

				return (
					typeof dto.email === 'string' &&
					isEmail(dto.email) &&
					(dto.name == null || typeof dto.name === 'string')
				);
			}

			return false;
		};

		if (Array.isArray(value)) {
			if (!allowMultiple) {
				return false;
			}

			return value.every((item) => isValid(item));
		}

		return isValid(value);
	}

	public defaultMessage(args: ValidationArguments): string {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const { allowMultiple = false } = (args.constraints?.[0] ?? {}) as {
			allowMultiple?: boolean;
		};
		return `${args.property} must be a valid email string or email object${allowMultiple ? ', or an array of valid email strings or objects' : ''}`;
	}
}

export class EmailAddressDto {
	@IsEmail()
	public email!: string;

	@IsString()
	@IsOptional()
	public name?: string;
}

export class EmailAttachmentDto {
	@IsString()
	public filename!: string;

	@IsString()
	public content!: string;

	@IsString()
	@IsOptional()
	public contentType?: string;

	@IsString()
	@IsOptional()
	public encoding?: string;

	@IsString()
	@IsOptional()
	public cid?: string;
}

export class SendEmailDto {
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public to!: EmailAddressDto | EmailAddressDto[] | string | string[];

	@IsOptional()
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public cc?: EmailAddressDto | EmailAddressDto[] | string | string[];

	@IsOptional()
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public bcc?: EmailAddressDto | EmailAddressDto[] | string | string[];

	@IsOptional()
	@Validate(IsEmailAddressDtoOrString)
	public from?: EmailAddressDto | string;

	@IsOptional()
	@Validate(IsEmailAddressDtoOrString)
	public replyTo?: EmailAddressDto | string;

	@IsString()
	public subject!: string;

	@IsString()
	@IsOptional()
	public text?: string;

	@IsString()
	@IsOptional()
	public html?: string;

	@ValidateNested({ each: true })
	@Type(() => EmailAttachmentDto)
	@IsArray()
	@IsOptional()
	public attachments?: EmailAttachmentDto[];

	@IsObject()
	@IsOptional()
	public headers?: Record<string, string>;

	@IsNumber()
	@IsOptional()
	public templateId?: number;

	@IsEnum(EmailTemplateSystemSlug)
	@IsOptional()
	public templateSystemSlug?: EmailTemplateSystemSlug;

	@IsObject()
	@IsOptional()
	public templateData?: Record<string, unknown>;

	@IsEnum(EmailProviderType)
	@IsOptional()
	public provider?: EmailProviderType;

	@IsOptional()
	public trackOpens?: boolean;

	@IsOptional()
	public trackClicks?: boolean;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	public tags?: string[];

	@IsObject()
	@IsOptional()
	public metadata?: Record<string, unknown>;

	@IsEnum(['high', 'normal', 'low'])
	@IsOptional()
	public priority?: 'high' | 'normal' | 'low';
}

export class SendBulkEmailDto {
	@ValidateNested({ each: true })
	@Type(() => SendEmailDto)
	@IsArray()
	public messages!: SendEmailDto[];

	@IsEnum(EmailProviderType)
	@IsOptional()
	public provider?: EmailProviderType;

	@IsOptional()
	public trackOpens?: boolean;

	@IsOptional()
	public trackClicks?: boolean;

	@IsArray()
	@IsString({ each: true })
	@IsOptional()
	public tags?: string[];

	@IsObject()
	@IsOptional()
	public metadata?: Record<string, unknown>;
}
